import { useEffect } from "react"
import { Link, Navigate, Routes, Route, useParams } from "react-router-dom"
import { Toaster } from "sonner"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { DashboardLayout } from "@/components/DashboardLayout"
import { ProtectedRoute } from "@/components/layout/ProtectedRoute"
import { isDashboard } from "@/lib/subdomain"
import { LoginPage } from "@/pages/auth/LoginPage"
import { SignUpPage } from "@/pages/auth/SignUpPage"
import { OnboardingPage } from "@/pages/auth/OnboardingPage"
import { WebsiteBuilderPage } from "@/pages/dashboard/WebsiteBuilderPage"
import { BlogPage } from "@/pages/dashboard/BlogPage"
import { EventsPage } from "@/pages/dashboard/EventsPage"
import { EventEditorPage } from "@/pages/dashboard/EventEditorPage"
import { EventRegistrationsPage } from "@/pages/dashboard/EventRegistrationsPage"
import { BlogPreviewPage } from "@/pages/dashboard/BlogPreviewPage"
import { MediaPage } from "@/pages/dashboard/MediaPage"
import { MediaEditorPage } from "@/pages/dashboard/MediaEditorPage"
import { MediaSeriesPage } from "@/pages/dashboard/MediaSeriesPage"
import { MediaSpeakersPage } from "@/pages/dashboard/MediaSpeakersPage"
import { MediaTopicsPage } from "@/pages/dashboard/MediaTopicsPage"
import { MediaSettingsPage } from "@/pages/dashboard/MediaSettingsPage"
import { DashboardPage } from "@/pages/dashboard/DashboardPage"
import { StripeConnectCallbackPage } from "@/pages/dashboard/StripeConnectCallbackPage"
import { CampaignsPage } from "@/pages/dashboard/CampaignsPage"
import { CampaignDetailPage } from "@/pages/dashboard/CampaignDetailPage"
import { ContactDetailPage } from "@/pages/dashboard/ContactDetailPage"
import { ContactsPage } from "@/pages/dashboard/ContactsPage"
import { SubmissionsInboxPage } from "@/pages/dashboard/SubmissionsInboxPage"
import { SettingsPage } from "@/pages/dashboard/SettingsPage"
import { OrgProfilePage } from "@/pages/dashboard/settings/OrgProfilePage"
import { TeamPage } from "@/pages/dashboard/TeamPage"
import { AdminDashboard } from "@/pages/admin/AdminDashboard"
import { SubdomainManagementPage } from "@/pages/admin/SubdomainManagementPage"
import { SponsorDashboardPage } from "@/pages/sponsor/SponsorDashboardPage"
import { SponsorOrgDetailPage } from "@/pages/sponsor/SponsorOrgDetailPage"
import { AcceptInvitePage } from "@/pages/AcceptInvitePage"
import { TransferAcceptPage } from "@/pages/TransferAcceptPage"
import { TransferWelcomePage } from "@/pages/TransferWelcomePage"
import { PublicNonprofitSitePage } from "@/pages/public/PublicNonprofitSitePage"
import { PublicWebsitePage } from "@/pages/public/PublicWebsitePage"
import { CampaignListingPage } from "@/pages/public/CampaignListingPage"
import { PublicCampaignDetailPage } from "@/pages/public/CampaignDetailPage"
import { FundraiserSharePage } from "@/pages/public/FundraiserSharePage"
import { BlogListingPage } from "@/pages/public/BlogListingPage"
import { BlogPostPage } from "@/pages/public/BlogPostPage"
import { EventsListingPage } from "@/pages/public/EventsListingPage"
import { EventDetailPage } from "@/pages/public/EventDetailPage"
import { MediaListingPage } from "@/pages/public/MediaListingPage"
import { MediaVideoPage } from "@/pages/public/MediaVideoPage"
import { useAuthStore } from "@/stores/authStore"

function BlogListingRoute() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  return <BlogListingPage orgSlug={orgSlug ?? ""} basePath={orgSlug ? `/${orgSlug}` : ""} />
}

function BlogPostRoute() {
  const { orgSlug, postSlug } = useParams<{ orgSlug: string; postSlug: string }>()
  return (
    <BlogPostPage
      orgSlug={orgSlug ?? ""}
      postSlug={postSlug ?? ""}
      basePath={orgSlug ? `/${orgSlug}` : ""}
    />
  )
}

function EventsListingRoute() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  return (
    <EventsListingPage
      orgSlug={orgSlug ?? ""}
      basePath={orgSlug ? `/${orgSlug}` : ""}
    />
  )
}

function EventDetailRoute() {
  const { orgSlug, eventSlug } = useParams<{ orgSlug: string; eventSlug: string }>()
  return (
    <EventDetailPage
      orgSlug={orgSlug ?? ""}
      eventSlug={eventSlug ?? ""}
      basePath={orgSlug ? `/${orgSlug}` : ""}
    />
  )
}

function MediaListingRoute() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  return (
    <MediaListingPage orgSlug={orgSlug ?? ""} basePath={orgSlug ? `/${orgSlug}` : ""} />
  )
}

function MediaVideoRoute() {
  const { orgSlug, videoSlug } = useParams<{ orgSlug: string; videoSlug: string }>()
  return (
    <MediaVideoPage
      orgSlug={orgSlug ?? ""}
      videoSlug={videoSlug ?? ""}
      basePath={orgSlug ? `/${orgSlug}` : ""}
    />
  )
}

function CampaignListingRoute() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  return (
    <CampaignListingPage orgSlug={orgSlug ?? ""} basePath={orgSlug ? `/${orgSlug}` : ""} />
  )
}

function CampaignPublicDetailRoute() {
  const { orgSlug, campaignSlug } = useParams<{ orgSlug: string; campaignSlug: string }>()
  return (
    <PublicCampaignDetailPage
      orgSlug={orgSlug ?? ""}
      campaignSlug={campaignSlug ?? ""}
      basePath={orgSlug ? `/${orgSlug}` : ""}
    />
  )
}

function FundraiserShareRoute() {
  const { orgSlug, campaignSlug, shareToken } = useParams<{
    orgSlug: string
    campaignSlug: string
    shareToken: string
  }>()
  return (
    <FundraiserSharePage
      orgSlug={orgSlug ?? ""}
      campaignSlug={campaignSlug ?? ""}
      shareToken={shareToken ?? ""}
      basePath={orgSlug ? `/${orgSlug}` : ""}
    />
  )
}

function HomePage() {
  const { user } = useAuthStore()
  if (user) {
    return <Navigate to="/dashboard" replace />
  }
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-muted/30 px-4">
      <h1 className="text-2xl font-semibold text-charity-forest">
        NOS
      </h1>
      <p className="text-muted-foreground text-center max-w-sm">
        Multi-tenant nonprofit donation platform. Sign in or create an account to get started.
      </p>
      <div className="flex gap-3">
        <Link
          to="/login"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Sign in
        </Link>
        <Link
          to="/signup"
          className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Sign up
        </Link>
      </div>
    </div>
  )
}

function App() {
  const initialize = useAuthStore((s) => s.initialize)
  const isOnDashboard = isDashboard()

  useEffect(() => {
    const unsubscribe = initialize()
    return unsubscribe
  }, [initialize])

  // Subdomain (e.g. testorg.getnos.ai) → public nonprofit site, no login
  if (!isOnDashboard) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <ErrorBoundary>
          <PublicNonprofitSitePage />
        </ErrorBoundary>
      </>
    )
  }

  // app.getnos.ai, localhost → dashboard routes (login required for staff)
  return (
    <>
      <Toaster position="top-center" richColors />
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          {/* NOS-194: Stripe Connect OAuth redirect URI (registered in Stripe) */}
          <Route
            path="/stripe/connect/callback"
            element={
              <ProtectedRoute>
                <StripeConnectCallbackPage />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard/donors" element={<Navigate to="/dashboard/contacts" replace />} />
          <Route path="/dashboard/donors/:donorId" element={<Navigate to="/dashboard/contacts" replace />} />
          <Route
            path="/dashboard/contacts"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ContactsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/contacts/:contactId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ContactDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/submissions"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SubmissionsInboxPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/ai-profile"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <OrgProfilePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/builder"
            element={
              <ProtectedRoute>
                <WebsiteBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard/customize" element={<Navigate to="/dashboard/builder" replace />} />
          <Route
            path="/dashboard/blog"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <BlogPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/blog/new"
            element={<Navigate to="/dashboard/builder" replace />}
          />
          <Route
            path="/dashboard/blog/categories"
            element={<Navigate to="/dashboard/builder" replace />}
          />
          <Route
            path="/dashboard/blog/:postId/edit"
            element={<Navigate to="/dashboard/builder" replace />}
          />
          <Route
            path="/dashboard/blog/:postId/preview"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <BlogPreviewPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/campaigns"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CampaignsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/campaigns/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CampaignDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/events"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <EventsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/events/new"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <EventEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/events/:eventId/edit"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <EventEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/events/:eventId/registrations"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <EventRegistrationsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/media"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MediaPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/media/new"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MediaEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/media/series"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MediaSeriesPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/media/speakers"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MediaSpeakersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/media/topics"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MediaTopicsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/media/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MediaSettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/media/:videoId/edit"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <MediaEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/team"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TeamPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/sponsor"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SponsorDashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/sponsor/orgs/:orgId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SponsorOrgDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AdminDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/subdomains"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SubdomainManagementPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/transfer-accept" element={<TransferAcceptPage />} />
          <Route
            path="/transfer-welcome"
            element={
              <ProtectedRoute>
                <TransferWelcomePage />
              </ProtectedRoute>
            }
          />
          {/* NOS-53, NOS-54: Public blog pages (path-based: app.getnos.ai/orgslug/blog) */}
          <Route
            path="/:orgSlug/blog"
            element={<BlogListingRoute />}
          />
          <Route
            path="/:orgSlug/blog/:postSlug"
            element={<BlogPostRoute />}
          />
          {/* NOS-47, NOS-48, NOS-49: Public events pages (path-based: app.getnos.ai/orgslug/events) */}
          <Route
            path="/:orgSlug/events"
            element={<EventsListingRoute />}
          />
          <Route
            path="/:orgSlug/events/:eventSlug"
            element={<EventDetailRoute />}
          />
          {/* NOS-128, NOS-129: Public media library (before /:orgSlug/:pageSlug catch-all) */}
          <Route path="/:orgSlug/media/:videoSlug" element={<MediaVideoRoute />} />
          <Route path="/:orgSlug/media" element={<MediaListingRoute />} />
          <Route
            path="/:orgSlug/campaign/:campaignSlug/fundraise/:shareToken"
            element={<FundraiserShareRoute />}
          />
          <Route path="/:orgSlug/campaign/:campaignSlug" element={<CampaignPublicDetailRoute />} />
          <Route path="/:orgSlug/campaign" element={<CampaignListingRoute />} />
          {/* NOS-58: Path-based routing for custom pages - /:orgSlug/:pageSlug */}
          <Route path="/:orgSlug/:pageSlug" element={<PublicWebsitePage />} />
          <Route path="/:orgSlug" element={<PublicWebsitePage />} />
          {/* NOS-241: nested slugs + stale imported source URLs (redirect map) */}
          <Route path="/:orgSlug/*" element={<PublicWebsitePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </>
  )
}

export default App
